import { t } from 'i18next';
import { Copy, Download, Eye, EyeOff } from 'lucide-react';
import React, { useLayoutEffect } from 'react';
import { createRoot } from 'react-dom/client';
import ReactJson from 'react-json-view';

import { useTheme } from '@/components/theme-provider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { isStepFileUrl, copyToClipboard } from '@/lib/utils';
import { isNil } from '@activepieces/shared';

import { Button } from './ui/button';
import { toast } from './ui/use-toast';

type JsonViewerProps = {
  json: any;
  title: string;
  hideDownload?: boolean;
};

type FileButtonProps = {
  fileUrl: string;
  handleDownloadFile: (fileUrl: string) => void;
};
const FileButton = ({ fileUrl, handleDownloadFile }: FileButtonProps) => {
  const readonly = fileUrl.includes('file://');
  return (
    <div className="flex items-center gap-0">
      <Button
        variant="ghost"
        size="sm"
        disabled={readonly}
        onClick={() => handleDownloadFile(fileUrl)}
        className="flex items-center gap-2 p-2 max-h-[20px] text-xs"
      >
        {readonly ? (
          <EyeOff className="w-4 h-4" />
        ) : (
          <Eye className="w-4 h-4" />
        )}
        {t('Download File')}
      </Button>
    </div>
  );
};

const removeDoubleQuotes = (str: string): string =>
  str.startsWith('"') && str.endsWith('"') ? str.slice(1, -1) : str;

const JsonViewer = React.memo(
  ({ json, title, hideDownload = false }: JsonViewerProps) => {
    const { theme } = useTheme();

    const lightTheme = {
      base00: 'rgba(0, 0, 0, 0)',
      base01: 'rgb(245, 245, 245)',
      base02: 'rgb(235, 235, 235)',
      base03: '#93a1a1',
      base04: 'rgba(0, 0, 0, 0.3)',
      base05: '#586e75',
      base06: '#073642',
      base07: '#002b36',
      base08: '#d33682',
      base09: '#0085ff',
      base0A: '#dc322f',
      base0B: '#859900',
      base0C: '#6c71c4',
      base0D: '#586e75',
      base0E: '#2aa198',
      base0F: '#9b59b6',
    };

    const darkTheme = {
      base00: '#000000',
      base01: '#303030',
      base02: '#505050',
      base03: '#b0b0b0',
      base04: '#d0d0d0',
      base05: '#e0e0e0',
      base06: '#f5f5f5',
      base07: '#ffffff',
      base08: '#fb0120',
      base09: '#0085ff',
      base0A: '#fda331',
      base0B: '#a1c659',
      base0C: '#76c7b7',
      base0D: '#6fb3d2',
      base0E: '#d381c3',
      base0F: '#be643c',
    };

    const viewerTheme = theme === 'dark' ? darkTheme : lightTheme;

    const handleCopy = async () => {
      const textToCopy = JSON.stringify(json, null, 2);
      const success = await copyToClipboard(textToCopy);

      if (success) {
        toast({
          variant: 'success',
          title: t('Copied to clipboard'),
          duration: 1000,
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('Failed to copy to clipboard'),
          duration: 3000,
        });
      }
    };

    const handleDownload = () => {
      const blob = new Blob([JSON.stringify(json, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      handleDownloadFile(url);
    };

    const handleDownloadFile = (fileUrl: string, ext = '') => {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `${title}${ext}`;
      link.click();
      URL.revokeObjectURL(fileUrl);
    };
    useLayoutEffect(() => {
      if (typeof json === 'object') {
        const stringValuesHTML = Array.from(
          document.getElementsByClassName('string-value'),
        );

        const stepFileUrlsHTML = stringValuesHTML.filter(
          (el) =>
            isStepFileUrl(el.textContent) ||
            isStepFileUrl(el.parentElement!.nextElementSibling?.textContent),
        );

        stepFileUrlsHTML.forEach((el: Element) => {
          // textContent, not innerHTML: innerHTML escapes & into &amp; and corrupts the URL query params
          const fileUrl = removeDoubleQuotes(el.textContent ?? '')
            .trim()
            .replace('\n', '');
          el.className += ' hidden';

          const rootElem = document.createElement('div');
          const root = createRoot(rootElem);

          el.parentElement!.replaceChildren(el as Node, rootElem as Node);
          const isProductionFile = fileUrl.includes('file://');

          root.render(
            <div data-file-root="true">
              {isProductionFile ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <FileButton
                        fileUrl={fileUrl}
                        handleDownloadFile={handleDownloadFile}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t('File is not available after execution.')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <FileButton
                  fileUrl={fileUrl}
                  handleDownloadFile={handleDownloadFile}
                />
              )}
            </div>,
          );
        });
      }
    });

    if (isStepFileUrl(json)) {
      return (
        <FileButton fileUrl={json} handleDownloadFile={handleDownloadFile} />
      );
    }

    return (
      <div className="rounded-lg border border-solid border-dividers overflow-hidden">
        <div className="px-3 py-2 flex border-solid border-b border-dividers justfy-center items-center">
          <div className="flex-grow justify-center items-center">
            <span className="text-md">{title}</span>
          </div>
          <div className="flex items-center gap-0">
            {!hideDownload && (
              <Button variant={'ghost'} size={'sm'} onClick={handleDownload}>
                <Download className="w-4 h-4" />
              </Button>
            )}
            <Button variant={'ghost'} size={'sm'} onClick={handleCopy}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {
          <>
            {isNil(json) ? (
              <pre className="text-sm whitespace-pre-wrap overflow-x-auto p-2">
                {json === null ? 'null' : 'undefined'}
              </pre>
            ) : (
              <>
                {typeof json !== 'string' && typeof json !== 'object' && (
                  <pre className="text-sm whitespace-pre-wrap overflow-x-auto p-2">
                    {JSON.stringify(json)}
                  </pre>
                )}
                {typeof json === 'string' && (
                  <pre className="text-sm whitespace-pre-wrap overflow-x-auto p-2">
                    {json}
                  </pre>
                )}
                {typeof json === 'object' && (
                  <ReactJson
                    style={{
                      overflowX: 'auto',
                      padding: '0.5rem',
                    }}
                    theme={viewerTheme}
                    enableClipboard={false}
                    groupArraysAfterLength={20}
                    displayDataTypes={false}
                    name={false}
                    quotesOnKeys={false}
                    collapsed={1}
                    src={json}
                  />
                )}
              </>
            )}
          </>
        }
      </div>
    );
  },
);

JsonViewer.displayName = 'JsonViewer';
export { JsonViewer };
